import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Moon, Sun, Monitor, Languages, Clock, Globe, Bell, Mail, Smartphone, MessageSquare, Link2, Calendar, Shield, LogOut, FileText, Trash2, Star, ShieldCheck, Download } from 'lucide-react';
import TimePicker from '../components/TimePicker';
import { setStoredToken } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { useI18n } from '../contexts/I18nContext';
import { useSettings } from '../contexts/SettingsContext';
import { usePWA } from '../contexts/PWAContext';
import { accountAPI, pushAPI } from '../utils/api';
import client from '../utils/api';
import { timezonesByGMT, getLocationTimezone, getCurrentTimezone } from '../utils/timezones';
import BottomNav from '../components/BottomNav';

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) outputArray[i] = rawData.charCodeAt(i);
  return outputArray;
}

const SettingsPage = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { theme, setTheme } = useTheme();
  const { t, lang, setLang, languages } = useI18n();
  const { settings, updateSetting } = useSettings();
  const { deferredPrompt: deferredInstallPrompt, isInstalled, isIOS, handleInstall } = usePWA();
  const [detectingLocation, setDetectingLocation] = useState(false);
  const [detectedCity, setDetectedCity] = useState('');
  const [currentTimezone, setCurrentTimezone] = useState('GMT+0');
  const [loading, setLoading] = useState(false);
  const [showFeedback, setShowFeedback] = useState(false);
  const [feedbackSubject, setFeedbackSubject] = useState('');
  const [feedbackMessage, setFeedbackMessage] = useState('');
  const [feedbackRating, setFeedbackRating] = useState(0);
  const [feedbackSent, setFeedbackSent] = useState(false);
  const [feedbackSending, setFeedbackSending] = useState(false);
  const [holidayCountries, setHolidayCountries] = useState([]);
  const [availableCountries, setAvailableCountries] = useState([]);
  const [showCalendarSync, setShowCalendarSync] = useState(false);
  const [hijriNewMonth, setHijriNewMonth] = useState(false);
  const [pushSupported, setPushSupported] = useState(false);
  const [pushSubscribed, setPushSubscribed] = useState(false);
  const [pushLoading, setPushLoading] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteSliderValue, setDeleteSliderValue] = useState(0);
  const [deleting, setDeleting] = useState(false);
  const deleteSliderRef = useRef(null);

  // Sync theme + language from context into their providers
  useEffect(() => { if (settings.theme) setTheme(settings.theme); }, [settings.theme]); // eslint-disable-line
  useEffect(() => { if (settings.language) setLang(settings.language); }, [settings.language]); // eslint-disable-line

  useEffect(() => {
    const init = async () => {
      try {
        const countries = await client.get('/api/holidays/countries').then(r => r.data).catch(() => []);
        setAvailableCountries(countries);
      } catch { }
      setHolidayCountries(settings.holidayCountries || []);

      const deviceTz = getCurrentTimezone();
      setCurrentTimezone(deviceTz);
      if (!settings.location || settings.location === 'auto') {
        detectLocation();
      } else {
        setDetectedCity(settings.location);
      setCurrentTimezone(settings.timezone === 'auto' ? getLocationTimezone(settings.location) : settings.timezone);
      }
      checkHijriNewMonth(settings.hijriAdjustment || 0);

      if ('serviceWorker' in navigator && 'PushManager' in window) {
        setPushSupported(true);
        try {
          const reg = await navigator.serviceWorker.getRegistration('/');
          if (reg) {
            const sub = await reg.pushManager.getSubscription();
            setPushSubscribed(!!sub);
          }
        } catch { /* ignore */ }
      }
    };
    init();
  }, []); // eslint-disable-line

  const checkHijriNewMonth = (adjustment) => {
    try {
      const today = new Date();
      const adjusted = new Date(today);
      adjusted.setDate(adjusted.getDate() + adjustment);
      // Simple check: use Intl to get Hijri day
      const hijriDay = new Intl.DateTimeFormat('en-u-ca-islamic', { day: 'numeric' }).format(adjusted);
      setHijriNewMonth(parseInt(hijriDay) === 1);
    } catch {
      setHijriNewMonth(false);
    }
  };

  const detectLocation = async () => {
    setDetectingLocation(true);
    try {
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          async (position) => {
            try {
              const response = await fetch(`https://nominatim.openstreetmap.org/reverse?lat=${position.coords.latitude}&lon=${position.coords.longitude}&format=json`);
              const data = await response.json();
              const city = data.address?.city || data.address?.town || data.address?.state || t('settings.unknown');
              setDetectedCity(city);
              // Save so we never ask again
              updateSetting('location', city);
            } catch {
              setDetectedCity('Location detected');
            }
            setDetectingLocation(false);
          },
          () => fetchIPLocation()
        );
      } else { fetchIPLocation(); }
    } catch { setDetectedCity('Auto'); setDetectingLocation(false); }
  };

  const fetchIPLocation = async () => {
    try {
      const response = await fetch('https://ipapi.co/json/');
      const data = await response.json();
      const city = data.city || data.region || t('settings.unknown');
      setDetectedCity(city);
      // Save so we never ask again
      updateSetting('location', city);
    } catch { setDetectedCity('Auto'); }
    setDetectingLocation(false);
  };

  const handleHijriAdjustment = (value) => {
    updateSetting('hijriAdjustment', value);
    checkHijriNewMonth(value);
  };

  const handleLanguageChange = (newLang) => {
    setLang(newLang);
    updateSetting('language', newLang);
  };

  const handleThemeChange = (newTheme) => {
    setTheme(newTheme);
    updateSetting('theme', newTheme);
  };

  const handleHolidayCountryToggle = (code) => {
    const updated = holidayCountries.includes(code)
      ? holidayCountries.filter(c => c !== code)
      : [...holidayCountries, code];
    setHolidayCountries(updated);
    updateSetting('holidayCountries', updated);
  };

  const handleLocationChange = async (value) => {
    if (value === 'auto') {
      setCurrentTimezone(getCurrentTimezone());
      updateSetting('location', 'auto');
      // Save actual IANA timezone (e.g. "Asia/Dubai") so backend can schedule correctly
      const ianaTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
      updateSetting('timezone', ianaTimezone);
      detectLocation();
    } else {
      const tz = getLocationTimezone(value);
      setCurrentTimezone(tz);
      setDetectedCity(value);
      updateSetting('location', value);
      updateSetting('timezone', tz);
    }
  };

  const handleFeedbackSubmit = async (e) => {
    e.preventDefault();
    setFeedbackSending(true);
    try {
      await client.post('/api/feedback', { subject: feedbackSubject, message: feedbackMessage, rating: feedbackRating || null });
      setFeedbackSent(true);
      setFeedbackSubject(''); setFeedbackMessage(''); setFeedbackRating(0);
      setTimeout(() => { setShowFeedback(false); setFeedbackSent(false); }, 2000);
    } catch (err) { console.error('Feedback error:', err); }
    finally { setFeedbackSending(false); }
  };

  const handleLogout = async () => { await logout(); navigate('/'); };

  const handlePushToggle = async () => {
    setPushLoading(true);
    try {
      if (pushSubscribed) {
        // Unsubscribe
        const reg = await navigator.serviceWorker.getRegistration('/');
        if (reg) {
          const sub = await reg.pushManager.getSubscription();
          if (sub) {
            await pushAPI.unsubscribe(sub.toJSON());
            await sub.unsubscribe();
          }
        }
        setPushSubscribed(false);
      } else {
        // Check if already blocked
        if (Notification.permission === 'denied') {
          alert('Notifications are blocked. Please go to your browser settings → Site Settings → Notifications and allow this site, then try again.');
          setPushLoading(false);
          return;
        }
        const permission = await Notification.requestPermission();
        if (permission !== 'granted') {
          alert('Notification permission was not granted. Please allow notifications and try again.');
          setPushLoading(false);
          return;
        }
        const reg = await navigator.serviceWorker.register('/sw-push.js', { scope: '/' });
        await navigator.serviceWorker.ready;
        const vapidRes = await pushAPI.getVapidKey();
        const publicKey = vapidRes?.publicKey || vapidRes?.data?.publicKey;
        if (!publicKey) throw new Error('VAPID key not available');
        const sub = await reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(publicKey)
        });
        await pushAPI.subscribe(sub.toJSON());
        setPushSubscribed(true);
      }
    } catch (err) {
      console.error('Push toggle error:', err);
      alert('Could not enable notifications: ' + (err.message || 'Unknown error'));
    } finally {
      setPushLoading(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (deleteSliderValue < 95) return;
    setDeleting(true);
    try {
      await accountAPI.delete();
      setStoredToken(null);          // clear JWT from localStorage immediately
      await logout();                // clear user state + cookies
      navigate('/', { replace: true });
    } catch (err) {
      console.error('Delete account error:', err);
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="w-full max-w-md mx-auto min-h-screen bg-[#F7F9FB] flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-slate-900 border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="w-full max-w-md mx-auto min-h-screen bg-[#F7F9FB] relative flex flex-col">
      <div className="p-6 pb-24 flex-1">
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
          <div className="mb-6">
            <h1 className="text-3xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>{t('settings.title')}</h1>
            <p className="text-base text-slate-600 mt-1" style={{ fontFamily: 'Figtree, sans-serif' }}>{t('settings.customize')}</p>
          </div>

          {/* Hijri New Month Notification */}
          {hijriNewMonth && (
            <div className="bg-emerald-50 border border-emerald-200 p-4 rounded-2xl mb-4 flex items-center gap-3" data-testid="hijri-new-month-banner">
              <Moon size={24} className="text-emerald-600 shrink-0" />
              <div>
                <p className="text-sm font-semibold text-emerald-800">{t('settings.new_hijri_month')}</p>
                <p className="text-xs text-emerald-600">{t('settings.new_hijri_desc')}</p>
              </div>
            </div>
          )}

          {/* User Info */}
          <div className="bg-white p-5 rounded-2xl shadow-[0_4px_20px_rgba(0,0,0,0.04)] mb-4" data-testid="user-info-card">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 bg-slate-900 rounded-full flex items-center justify-center text-white text-xl font-bold">
                {(user?.name?.charAt(0) || user?.email?.charAt(0) || '?').toUpperCase()}
              </div>
              <div>
                <h3 className="text-lg font-semibold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>{user?.name || user?.email}</h3>
                <p className="text-sm text-slate-500" style={{ fontFamily: 'Figtree, sans-serif' }}>{user?.email}</p>
                {user?.subscription?.tier === 'premium' && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-100 text-amber-700 rounded-full text-xs font-semibold mt-1"><Star size={10} />Premium</span>
                )}
              </div>
            </div>
          </div>

          {/* Theme */}
          <div className="bg-white p-5 rounded-2xl shadow-[0_4px_20px_rgba(0,0,0,0.04)] mb-4">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-slate-100 text-slate-700 rounded-xl flex items-center justify-center">
                {theme === 'dark' ? <Moon size={20} /> : theme === 'light' ? <Sun size={20} /> : <Monitor size={20} />}
              </div>
              <div><h3 className="text-base font-semibold text-slate-900">{t('settings.theme')}</h3></div>
            </div>
            <div className="grid grid-cols-3 gap-2" data-testid="theme-selector">
              {[
                { value: 'light', label: t('settings.light'), icon: Sun },
                { value: 'dark', label: t('settings.dark'), icon: Moon },
                { value: 'system', label: t('settings.system'), icon: Monitor },
              ].map(opt => (
                <button key={opt.value} onClick={() => handleThemeChange(opt.value)} data-testid={`theme-${opt.value}`}
                  className={`py-2.5 rounded-xl font-semibold text-sm transition-all flex items-center justify-center gap-1.5 ${
                    theme === opt.value ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-600'
                  }`}>
                  <opt.icon size={14} />{opt.label}
                </button>
              ))}
            </div>
          </div>

          {/* Language */}
          <div className="bg-white p-5 rounded-2xl shadow-[0_4px_20px_rgba(0,0,0,0.04)] mb-4">
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-indigo-100 text-indigo-700 rounded-xl flex items-center justify-center"><Languages size={20} /></div>
                <h3 className="text-base font-semibold text-slate-900">{t('settings.language')}</h3>
              </div>
              <select
                value={lang}
                onChange={(e) => handleLanguageChange(e.target.value)}
                data-testid="language-select"
                className="bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-sm font-semibold text-slate-800 focus:outline-none focus:border-indigo-400 cursor-pointer"
              >
                {Object.entries(languages).map(([code, name]) => (
                  <option key={code} value={code}>{name}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Reminder Time */}
          <div className="bg-white p-5 rounded-2xl shadow-[0_4px_20px_rgba(0,0,0,0.04)] mb-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-blue-100 text-blue-700 rounded-xl flex items-center justify-center"><Clock size={20} /></div>
              <div className="flex-1"><h3 className="text-base font-semibold text-slate-900">{t('settings.reminder_time')}</h3><p className="text-sm text-slate-500">{t('settings.reminder_time_desc')}</p></div>
            </div>
            {(() => {
              // Derive 12h components from settings (new fields preferred, legacy fallback)
              const h24 = settings.default_reminder_hour != null
                ? Number(settings.default_reminder_hour)
                : Number((settings.defaultReminderTime || '09:00').split(':')[0]);
              const rawMin = settings.default_reminder_hour != null
                ? (settings.default_reminder_minute || 0)
                : Number((settings.defaultReminderTime || '09:00').split(':')[1] || 0);
              const hour = h24 === 0 ? 12 : h24 > 12 ? h24 - 12 : h24;
              const minute = [0, 30].includes(rawMin) ? rawMin : 0;
              const period = h24 < 12 ? 'AM' : 'PM';
              return (
                <TimePicker
                  hour={hour} minute={minute} period={period}
                  testIdPrefix="default"
                  onChange={({ hour: h, minute: m, period: p }) => {
                    const h24out = p === 'PM' && h !== 12 ? h + 12 : p === 'AM' && h === 12 ? 0 : h;
                    updateSetting('default_reminder_hour', h24out);
                    updateSetting('default_reminder_minute', m);
                  }}
                />
              );
            })()}
          </div>

          {/* Location */}
          <div className="bg-white p-5 rounded-2xl shadow-[0_4px_20px_rgba(0,0,0,0.04)] mb-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-purple-100 text-purple-700 rounded-xl flex items-center justify-center"><Globe size={20} /></div>
              <div className="flex-1"><h3 className="text-base font-semibold text-slate-900">{t('settings.location')}</h3>
                <p className="text-sm text-slate-500">{detectingLocation ? t('settings.detecting') : `${detectedCity || t('settings.auto_detect')} - ${currentTimezone}`}</p>
              </div>
            </div>
            <select value={settings.location} onChange={(e) => handleLocationChange(e.target.value)} data-testid="location-select"
              className="w-full bg-slate-50 border border-transparent focus:border-slate-300 focus:bg-white rounded-xl px-4 py-3 text-slate-900 transition-colors text-sm">
              <option value="auto">{t('settings.auto_detect')}</option>
              {timezonesByGMT.map((tzGroup) => (<optgroup key={tzGroup.gmt} label={tzGroup.gmt}>{tzGroup.locations.map((loc) => (<option key={loc} value={loc}>{loc}</option>))}</optgroup>))}
            </select>
          </div>

          {/* Hijri Adjustment */}
          <div className="bg-white p-5 rounded-2xl shadow-[0_4px_20px_rgba(0,0,0,0.04)] mb-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-emerald-100 text-emerald-700 rounded-xl flex items-center justify-center"><Moon size={20} /></div>
              <div className="flex-1"><h3 className="text-base font-semibold text-slate-900">{t('settings.hijri_adjust')}</h3><p className="text-sm text-slate-500">{t('settings.hijri_desc')}</p></div>
            </div>
            <div className="flex items-center gap-2" data-testid="hijri-adjustment-buttons">
              {[-2, -1, 0, 1, 2].map((value) => (
                <button key={value} onClick={() => handleHijriAdjustment(value)}
                  className={`flex-1 py-2 rounded-xl font-semibold text-sm transition-all ${settings.hijriAdjustment === value ? 'bg-emerald-500 text-white' : 'bg-slate-100 text-slate-600'}`}>
                  {value > 0 ? '+' : ''}{value}
                </button>
              ))}
            </div>
          </div>

          {/* Persian Calendar */}
          <div className="bg-white p-5 rounded-2xl shadow-[0_4px_20px_rgba(0,0,0,0.04)] mb-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-orange-100 text-orange-700 rounded-xl flex items-center justify-center"><Calendar size={20} /></div>
                <div><h3 className="text-base font-semibold text-slate-900">{t('settings.persian_calendar') || 'Persian Calendar'}</h3>
                  <p className="text-sm text-slate-500">{t('settings.persian_desc') || 'Show Jalali date on Home'}</p></div>
              </div>
              <button onClick={() => updateSetting('persianCalendarEnabled', !settings.persianCalendarEnabled)} data-testid="persian-calendar-toggle"
                className={`w-12 h-7 rounded-full transition-colors relative ${settings.persianCalendarEnabled ? 'bg-orange-500' : 'bg-slate-300'}`}>
                <div className={`w-5 h-5 bg-white rounded-full absolute top-1 transition-transform ${settings.persianCalendarEnabled ? 'translate-x-6' : 'translate-x-1'}`} />
              </button>
            </div>
          </div>

          {/* Calendar Sync */}
          <div className="bg-white p-5 rounded-2xl shadow-[0_4px_20px_rgba(0,0,0,0.04)] mb-4 opacity-60">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-cyan-100 text-cyan-700 rounded-xl flex items-center justify-center"><Link2 size={20} /></div>
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <h3 className="text-base font-semibold text-slate-900">{t('settings.calendar_sync')}</h3>
                  <span className="text-xs bg-slate-100 text-slate-500 px-2 py-0.5 rounded-full font-medium">{t('settings.coming_later')}</span>
                </div>
                <p className="text-sm text-slate-400">{t('settings.sync_desc')}</p>
              </div>
            </div>
            <div className="space-y-2 mt-3">
              {['Google Calendar', 'Apple Calendar', 'Outlook Calendar'].map(cal => (
                <div key={cal} className="w-full p-3 rounded-xl border border-slate-100 bg-slate-50 text-sm font-medium text-slate-400 flex items-center justify-between cursor-default">
                  {cal}
                  <span className="text-xs text-slate-300">{t('settings.coming_later')}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Install App — PWA */}
          {!isInstalled && (
            <div className="bg-white dark:bg-slate-800 border border-indigo-100 dark:border-indigo-900 p-5 rounded-2xl shadow-[0_4px_20px_rgba(99,102,241,0.08)] mb-4" data-testid="install-app-card">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 bg-indigo-600 text-white rounded-xl flex items-center justify-center shadow-sm">
                  <Download size={20} />
                </div>
                <div>
                  <h3 className="text-base font-semibold text-slate-900 dark:text-white">{t('settings.install_app')}</h3>
                  <p className="text-sm text-slate-500 dark:text-slate-400">{t('settings.install_desc')}</p>
                </div>
              </div>

              {deferredInstallPrompt && (
                <button
                  onClick={handleInstall}
                  data-testid="install-app-btn"
                  className="w-full py-2.5 bg-indigo-600 text-white rounded-xl font-semibold text-sm active:scale-[0.97] transition-all shadow-sm"
                >
                  {t('settings.install_btn')}
                </button>
              )}

              {isIOS && !deferredInstallPrompt && (
                <div className="space-y-2">
                  {[
                    { icon: '↑', text: t('settings.install_ios_step1') },
                    { icon: '+', text: t('settings.install_ios_step2') },
                    { icon: '✓', text: t('settings.install_ios_step3') },
                  ].map(({ icon, text }, i) => (
                    <div key={i} className="flex items-center gap-3">
                      <div className="w-6 h-6 bg-indigo-100 dark:bg-indigo-900 text-indigo-700 dark:text-indigo-300 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0">{icon}</div>
                      <p className="text-sm text-slate-700 dark:text-slate-300">{text}</p>
                    </div>
                  ))}
                </div>
              )}

              {!deferredInstallPrompt && !isIOS && (
                <div className="text-center space-y-1">
                  <p className="text-xs text-slate-500">Look for the <strong>⊕</strong> icon in Chrome's address bar</p>
                  <p className="text-xs text-slate-400">or tap Chrome menu (⋮) → "Install iRemindit"</p>
                </div>
              )}
            </div>
          )}

          {isInstalled && (
            <div className="bg-green-50 border border-green-100 p-4 rounded-2xl mb-4 flex items-center gap-3" data-testid="app-installed-banner">
              <div className="w-8 h-8 bg-green-100 rounded-xl flex items-center justify-center"><Download size={16} className="text-green-600" /></div>
              <div>
                <p className="text-sm font-semibold text-green-800">{t('settings.installed')}</p>
                <p className="text-xs text-green-600">{t('settings.installed_desc')}</p>
              </div>
            </div>
          )}

          {/* Combined Notifications */}
          <div className="bg-white p-5 rounded-2xl shadow-[0_4px_20px_rgba(0,0,0,0.04)] mb-4">
            {/* Card header */}
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-blue-100 text-blue-700 rounded-xl flex items-center justify-center">
                <Bell size={20} />
              </div>
              <div>
                <h3 className="text-base font-semibold text-slate-900">{t('settings.notifications')}</h3>
                <p className="text-sm text-slate-500">{t('settings.notif_desc')}</p>
              </div>
            </div>

            <div className="border-t border-slate-100 pt-4 space-y-4">
              {/* Email row — always visible */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-blue-50 text-blue-600 rounded-lg flex items-center justify-center">
                    <Mail size={16} />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-slate-800">{t('settings.email_label')}</p>
                    <p className="text-xs text-slate-400">{t('settings.email_notif_desc')}</p>
                  </div>
                </div>
                <button
                  onClick={() => updateSetting('notificationsEnabled', !settings.notificationsEnabled)}
                  data-testid="notification-toggle"
                  className={`w-12 h-7 rounded-full transition-colors relative flex-shrink-0 ${settings.notificationsEnabled ? 'bg-blue-600' : 'bg-slate-200'}`}
                >
                  <div className={`w-5 h-5 bg-white rounded-full absolute top-1 shadow-sm transition-transform ${settings.notificationsEnabled ? 'translate-x-6' : 'translate-x-1'}`} />
                </button>
              </div>

              {/* Mobile/Push row — only on devices that support push */}
              {pushSupported && (
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-violet-50 text-violet-600 rounded-lg flex items-center justify-center">
                      <Smartphone size={16} />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-slate-800">{t('settings.mobile_label')}</p>
                      <p className="text-xs text-slate-400">{t('settings.push_desc')}</p>
                    </div>
                  </div>
                  <button
                    onClick={handlePushToggle}
                    disabled={pushLoading}
                    data-testid="push-notification-toggle"
                    className={`w-12 h-7 rounded-full transition-colors relative flex-shrink-0 ${pushSubscribed ? 'bg-violet-600' : 'bg-slate-200'} ${pushLoading ? 'opacity-50' : ''}`}
                  >
                    <div className={`w-5 h-5 bg-white rounded-full absolute top-1 shadow-sm transition-transform ${pushSubscribed ? 'translate-x-6' : 'translate-x-1'}`} />
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Feedback */}
          <button onClick={() => setShowFeedback(true)} data-testid="feedback-button"
            className="w-full bg-white p-5 rounded-2xl shadow-[0_4px_20px_rgba(0,0,0,0.04)] mb-4 flex items-center gap-3 active:scale-[0.98] transition-all text-left">
            <div className="w-10 h-10 bg-amber-100 rounded-xl flex items-center justify-center"><MessageSquare size={20} className="text-amber-700" /></div>
            <div className="flex-1"><h3 className="text-base font-semibold text-slate-900">{t('settings.feedback')}</h3><p className="text-sm text-slate-500">{t('settings.feedback_desc')}</p></div>
          </button>

          {/* Terms */}
          <button onClick={() => navigate('/terms')} data-testid="terms-button"
            className="w-full bg-white p-5 rounded-2xl shadow-[0_4px_20px_rgba(0,0,0,0.04)] mb-4 flex items-center gap-3 active:scale-[0.98] transition-all text-left">
            <div className="w-10 h-10 bg-slate-100 rounded-xl flex items-center justify-center"><FileText size={20} className="text-slate-700" /></div>
            <div className="flex-1"><h3 className="text-base font-semibold text-slate-900">{t('settings.terms')}</h3></div>
          </button>

          {/* Privacy */}
          <button onClick={() => navigate('/privacy')} data-testid="privacy-policy-button"
            className="w-full bg-white p-5 rounded-2xl shadow-[0_4px_20px_rgba(0,0,0,0.04)] mb-4 flex items-center gap-3 active:scale-[0.98] transition-all text-left">
            <div className="w-10 h-10 bg-green-100 rounded-xl flex items-center justify-center"><Shield size={20} className="text-green-700" /></div>
            <div className="flex-1"><h3 className="text-base font-semibold text-slate-900">{t('settings.privacy')}</h3></div>
          </button>

          {/* Admin Portal */}
          {user?.role === 'admin' && (
            <button onClick={() => navigate('/admin')} data-testid="admin-portal-button"
              className="w-full bg-white p-5 rounded-2xl shadow-[0_4px_20px_rgba(0,0,0,0.04)] mb-4 flex items-center gap-3 active:scale-[0.98] transition-all text-left border border-amber-200">
              <div className="w-10 h-10 bg-amber-100 rounded-xl flex items-center justify-center"><ShieldCheck size={20} className="text-amber-700" /></div>
              <div className="flex-1"><h3 className="text-base font-semibold text-slate-900">{t('settings.admin')}</h3><p className="text-sm text-slate-500">{t('settings.admin_desc')}</p></div>
            </button>
          )}

          {/* GDPR Delete Account */}
          <div className="bg-white p-5 rounded-2xl shadow-[0_4px_20px_rgba(0,0,0,0.04)] mb-4 border border-red-100">
            <button onClick={() => setShowDeleteConfirm(!showDeleteConfirm)} data-testid="delete-account-button"
              className="w-full flex items-center gap-3 text-left">
              <div className="w-10 h-10 bg-red-100 rounded-xl flex items-center justify-center"><Trash2 size={20} className="text-red-600" /></div>
              <div className="flex-1">
                <h3 className="text-base font-semibold text-red-600">{t('settings.delete_account') || 'Delete Account'}</h3>
              </div>
            </button>
            {showDeleteConfirm && (
              <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} className="mt-4 overflow-hidden">
                <div className="bg-red-50 p-4 rounded-xl mb-4">
                  <p className="text-sm text-red-700 font-medium mb-1">{t('settings.delete_warning') || 'This action cannot be undone!'}</p>
                  <p className="text-xs text-red-600">{t('settings.delete_warning_desc') || 'All your reminders, groups, and settings will be permanently deleted.'}</p>
                </div>
                <p className="text-xs text-slate-500 mb-3 text-center font-medium">{t('settings.slide_to_delete') || 'Slide to delete your account'}</p>
                <div className="relative h-14 bg-red-100 rounded-full overflow-hidden" data-testid="delete-slider-track">
                  <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-10">
                    <span className={`text-sm font-semibold transition-opacity ${deleteSliderValue > 50 ? 'text-white' : 'text-red-400'}`}>
                      {deleteSliderValue >= 95 ? (t('settings.release_to_delete') || 'Release to delete') : (t('settings.slide_right') || 'Slide right to confirm')}
                    </span>
                  </div>
                  <div className="absolute inset-y-0 left-0 bg-red-500 rounded-full transition-all"
                    style={{ width: `${Math.max(deleteSliderValue, 5)}%` }} />
                  <input
                    ref={deleteSliderRef}
                    type="range" min="0" max="100" value={deleteSliderValue}
                    onChange={(e) => setDeleteSliderValue(parseInt(e.target.value))}
                    onMouseUp={() => { if (deleteSliderValue >= 95) handleDeleteAccount(); else setDeleteSliderValue(0); }}
                    onTouchEnd={() => { if (deleteSliderValue >= 95) handleDeleteAccount(); else setDeleteSliderValue(0); }}
                    data-testid="delete-slider"
                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-20"
                    disabled={deleting}
                  />
                </div>
                {deleting && <p className="text-xs text-red-600 text-center mt-2 animate-pulse">{t('settings.deleting') || 'Deleting your account...'}</p>}
              </motion.div>
            )}
          </div>

          {/* Logout */}
          <button onClick={handleLogout} data-testid="logout-button"
            className="w-full bg-white p-5 rounded-2xl shadow-[0_4px_20px_rgba(0,0,0,0.04)] flex items-center gap-3 active:scale-[0.98] transition-all text-red-600">
            <div className="w-10 h-10 bg-red-100 rounded-xl flex items-center justify-center"><LogOut size={20} /></div>
            <span className="text-base font-semibold">{t('settings.logout')}</span>
          </button>
        </motion.div>
      </div>

      {/* Feedback Modal */}
      {showFeedback && (
        <div className="fixed inset-0 bg-black/50 flex items-end justify-center" style={{ zIndex: 10000 }} onClick={(e) => { if (e.target === e.currentTarget) setShowFeedback(false); }}>
          <motion.div initial={{ y: 100, opacity: 0 }} animate={{ y: 0, opacity: 1 }} className="bg-white w-full max-w-md rounded-t-3xl flex flex-col mb-16" style={{ maxHeight: 'calc(90vh - 64px)' }}>
            {/* Header */}
            <div className="px-6 pt-6 pb-4 flex-shrink-0">
              <div className="w-10 h-1 bg-slate-200 rounded-full mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>{t('feedback.title')}</h2>
            </div>

            {feedbackSent ? (
              <div className="text-center py-10 px-6 flex-1">
                <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4"><MessageSquare size={32} className="text-emerald-600" /></div>
                <p className="text-lg font-semibold text-slate-900">{t('feedback.thanks')}</p>
                <p className="text-sm text-slate-500 mt-1">{t('feedback.thanks_desc')}</p>
              </div>
            ) : (
              <form onSubmit={handleFeedbackSubmit} className="flex flex-col flex-1 overflow-hidden">
                {/* Scrollable content */}
                <div className="px-6 overflow-y-auto flex-1 space-y-4 pb-2">
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">{t('feedback.rating')}</label>
                    <div className="flex gap-2">
                      {[1, 2, 3, 4, 5].map(s => (
                        <button key={s} type="button" onClick={() => setFeedbackRating(s)} data-testid={`rating-${s}`}
                          className={`flex-1 h-10 rounded-xl text-sm font-bold transition-all ${feedbackRating >= s ? 'bg-amber-400 text-white' : 'bg-slate-100 text-slate-400 hover:bg-slate-200'}`}>
                          {s}
                        </button>
                      ))}
                    </div>
                  </div>
                  <input type="text" required value={feedbackSubject} onChange={(e) => setFeedbackSubject(e.target.value)} placeholder={t('feedback.subject')} data-testid="feedback-subject"
                    className="w-full bg-slate-50 border border-transparent focus:border-slate-300 focus:bg-white rounded-xl px-4 py-3 text-slate-900 placeholder:text-slate-400 outline-none" />
                  <textarea required value={feedbackMessage} onChange={(e) => setFeedbackMessage(e.target.value)} placeholder={t('feedback.message')} rows={4} data-testid="feedback-message"
                    className="w-full bg-slate-50 border border-transparent focus:border-slate-300 focus:bg-white rounded-xl px-4 py-3 text-slate-900 placeholder:text-slate-400 resize-none outline-none" />
                </div>

                {/* Sticky action buttons — always visible above keyboard */}
                <div className="px-6 py-4 border-t border-slate-100 flex gap-3 flex-shrink-0 bg-white">
                  <button type="button" onClick={() => setShowFeedback(false)}
                    className="flex-1 bg-slate-100 text-slate-900 py-3 rounded-full font-semibold text-sm">
                    {t('feedback.cancel')}
                  </button>
                  <button type="submit" disabled={feedbackSending} data-testid="feedback-submit"
                    className="flex-1 bg-slate-900 text-white py-3 rounded-full font-semibold text-sm disabled:opacity-50">
                    {feedbackSending ? t('feedback.sending') : t('feedback.submit')}
                  </button>
                </div>
              </form>
            )}
          </motion.div>
        </div>
      )}

      <BottomNav />
    </div>
  );
};

export default SettingsPage;
